/**
 * What the reader will actually get — the article, in the reader's renderer,
 * at three widths (#1744 point 5).
 *
 * ── WHY THIS IS NOT A SECOND RENDERER ───────────────────────────────────────
 * The point of a preview is that it is not a separate opinion about the
 * article. So this imports `KbArticleBody` from `@solum/ui` — the SAME
 * component `apps/portal/src/pages/Knowledge.tsx` renders for the customer —
 * and reads the SAME door, `service.kb_article_assets`, with the same argument.
 * There is no console-side markdown, no console-side figure resolution and no
 * console-side idea of what a `{{fig:…}}` marker means. If the two ever
 * disagreed it would be because the SERVER answered the two callers
 * differently, which is a governance fact worth seeing rather than a rendering
 * bug worth hiding.
 *
 * `the-preview-is-the-readers-view.test.ts` asserts that structurally: same
 * component, same door, same argument.
 *
 * ── IT STREAMS THE REAL BYTES, THROUGH THE ONE SHARED TRANSPORT ────────────
 * An earlier version of this file could not: the console had no storage
 * transport at all, and copying the portal's was what the portal's own header
 * argues against — *"a new file card cannot accidentally re-introduce a
 * transferable URL merely by copying a sibling"*. That was reported rather than
 * taken, and ruled at mailbox 54560: ONE host-neutral helper in `@solum/sdk`,
 * with the portal's existing fetches migrated onto it in the same slice, so the
 * end state is one implementation rather than new-shared-plus-old-copies.
 *
 * So a figure here is the same bytes, fetched the same way, verified by the
 * same `verifiedKbAssetBlob` the customer's page uses:
 *
 *   1. `service.kb_asset_ticket` re-asks visibility, WRITES the issuance
 *      receipt, and answers with the exact object key. Absent, superseded,
 *      quarantined and not-yours all come back as NO ROWS, so the ticket is not
 *      an existence oracle — and this client must not try to tell them apart
 *      either.
 *   2. `fetchAuthenticatedObject` performs one authenticated GET with THIS
 *      caller's own bearer. No signed URL is minted, so there is no
 *      transferable artefact to paste into a chat, a ticket or a log.
 *
 * The blob URL is document-scoped and revoked on replacement and on unmount by
 * `objectUrlHolder` — a leak that lives as long as the page is the failure that
 * arrives quietly.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, KbArticleBody, verifiedKbAssetBlob, type KbAsset } from '@solum/ui'
import { fetchAuthenticatedObject, objectUrlHolder } from '@solum/sdk'
import { useSolum, storageAccess } from '../data/client'
import { QueryBoundary, queryState } from '../data/QueryBoundary'
import s from './KbArticlePreview.module.css'

/**
 * The three widths, and why these three.
 *
 * They are the widths the estate already tests its customer surfaces at rather
 * than three round numbers: 390 is the phone `apps/portal`'s own browser suite
 * pins, 768 is the tablet break the shared shell changes layout on, and 1120 is
 * a desktop reading column. A preview at widths nothing else uses would agree
 * with no other evidence in the repository.
 */
const WIDTHS = [
  { key: 'phone', label: 'Phone', px: 390 },
  { key: 'tablet', label: 'Tablet', px: 768 },
  { key: 'desktop', label: 'Desktop', px: 1120 },
] as const
type WidthKey = (typeof WIDTHS)[number]['key']

export interface KbPreviewArticle {
  body: string | null
  body_document?: unknown
  status: string
  audience: string | null
  title: string | null
}

/** The console holds no byte transport, and says so rather than pretending. */
/**
 * One figure's bytes, as a document-scoped blob URL.
 *
 * `KbArticleBody` asks for a URL per figure and may ask again when a figure
 * remounts, so each call gets its own holder and each holder revokes what it
 * replaces. The holders are released together when the preview unmounts.
 */
function useFigureUrls() {
  const holders = useRef(new Map<string, ReturnType<typeof objectUrlHolder>>())
  useEffect(() => {
    const live = holders.current
    return () => { for (const h of live.values()) h.release(); live.clear() }
  }, [])
  return holders
}

export function KbArticlePreview({ id }: { id: string }): ReactNode {
  const { client } = useSolum()
  const raw = client.raw()
  const holders = useFigureUrls()

  /* Ticket, then bytes. Both steps carry THIS caller's session, and the ticket
     step is what writes the receipt — so a figure cannot be looked at without
     the server recording that it was. */
  const requestUrl = async ({ articleId, assetKey }: { articleId: string; assetKey: string }) => {
    /* The ticket carries the VERIFIED facts as well as the key, and
       `verifiedKbAssetBlob` needs them — it refuses bytes whose delivered
       content type or length disagrees with what the server sealed. */
    const rows = await raw.rpcTable<{
      bucket?: string; storage_path?: string; content_type?: unknown; size_bytes?: unknown
    }>(
      'kb_asset_ticket', { p_article_id: articleId, p_asset_key: assetKey }, 'service')
    const ticket = rows[0]
    // No rows is the ONE answer for absent, superseded, quarantined and
    // not-yours. This client must not try to tell them apart either.
    if (!ticket?.storage_path) throw new Error('that figure is not available')

    const blob = await fetchAuthenticatedObject(
      { bucket: String(ticket.bucket ?? 'service-kb-assets'), storage_path: String(ticket.storage_path) },
      { ...storageAccess, verify: (res) => verifiedKbAssetBlob(res, ticket) })

    const key = `${articleId}:${assetKey}`
    let holder = holders.current.get(key)
    if (!holder) { holder = objectUrlHolder(); holders.current.set(key, holder) }
    return holder.set(blob)
  }

  const article = useQuery({
    queryKey: ['kb', 'preview-article', id],
    queryFn: async () => (await raw.select<Record<string, unknown>>(
      'service', 'kb_article', { where: { id } }))[0] ?? null,
  })
  /* The reader's own manifest door, called exactly as the portal calls it. */
  const assets = useQuery({
    queryKey: ['kb', 'assets', id],
    queryFn: () => raw.rpcTable<KbAsset>('kb_article_assets', { p_article_id: id }, 'service'),
  })

  /* `queryState` returns an OBJECT and never null, so `if (state)` is ALWAYS
     true — the shape `KbArticleProcess` gets right and I got wrong in all three
     of these panels. Every one of them rendered an empty boundary for ever, and
     987 unit tests stayed green because they drive the PURE view directly.
     A browser found it in one navigation. Guard on `.kind`. */
  const gate = queryState([article])
  const a = article.data
  if (gate.kind !== 'ready' || !a) {
    return (
      <QueryBoundary queries={[article]} label="this article’s preview">
        {() => null}
      </QueryBoundary>
    )
  }

  return (
    <KbPreviewView
      articleId={id}
      requestUrl={requestUrl}
      article={{
        body: typeof a.body === 'string' ? a.body : null,
        body_document: a.body_document,
        status: String(a.status ?? ''),
        audience: a.audience == null ? null : String(a.audience),
        title: a.title == null ? null : String(a.title),
      }}
      assets={assets.data ?? []}
      assetsLoading={assets.isLoading}
      assetsFailed={assets.isError}
    />
  )
}

/**
 * The pure half, exported so the property can be asserted with no client and no
 * providers — the same split `KbArticleProcess` uses and for the same reason.
 */
export function KbPreviewView({
  articleId, article, assets, assetsLoading = false, assetsFailed = false,
  initialWidth = 'desktop', requestUrl,
}: {
  articleId: string
  article: KbPreviewArticle
  assets: KbAsset[]
  assetsLoading?: boolean
  assetsFailed?: boolean
  initialWidth?: WidthKey
  /* Injected, so the pure view can be rendered with no client at all. Absent
     means "this surface cannot fetch bytes", which is a legitimate state and
     the one every unit test below uses. */
  requestUrl?: (input: { articleId: string; assetKey: string }) => Promise<string>
}): ReactNode {
  const [width, setWidth] = useState<WidthKey>(initialWidth)
  const chosen = WIDTHS.find((w) => w.key === width) ?? WIDTHS[2]

  /* WHO this preview is of, derived from the row rather than from a persona or
     from who is looking. `audience` is the server's word for reach and
     `status` is the server's word for whether it has been released; the
     sentence below is those two facts said in English and nothing else. */
  const reachesCustomers = article.audience === 'customer'
  const released = article.status === 'published'
  const audienceLine = reachesCustomers
    ? 'This is the customer’s view.'
    : 'This is a colleague’s view — this article is internal and no customer can reach it.'
  const releaseLine = released
    ? 'It is published, so this is what a reader gets right now.'
    : `It is ${article.status || 'unreleased'}, so no reader can see it yet — this is what they would get if it were published.`

  return (
    <section className={s.preview} data-testid="kb-preview" aria-label="Article preview">
      <header className={s.panelHead}>
        <div className={s.widths} role="group" aria-label="Preview width">
          {/* The SHARED Button, not a native one. `experience-adoption`'s
              rawButton baseline may only move down, and it is right to hold
              that line: a segmented control is still a button, and the focus
              ring, the hit target and the loading shape belong to the control
              rather than to this file. `aria-pressed` rides through because
              ButtonProps extends the native attributes. */}
          {WIDTHS.map((w) => (
            <Button
              key={w.key}
              variant={w.key === width ? 'secondary' : 'ghost'}
              className={w.key === width ? s.widthOn : s.width}
              aria-pressed={w.key === width}
              onClick={() => setWidth(w.key)}
              data-testid={`kb-preview-${w.key}`}
            >{w.label} <span className={s.px}>{w.px}px</span></Button>
          ))}
        </div>
        {/* Said plainly and unconditionally: a reader of this surface must never
            have to work out whether they are looking at the customer's view. */}
        <p className={s.audience} data-testid="kb-preview-audience">
          {audienceLine} {releaseLine}
        </p>
      </header>

      {/* The width is applied to a FRAME, not to the component: KbArticleBody
          is the reader's renderer and gets no console-specific styling at all.
          `max-width: 100%` so a 1120px preview inside a narrower pane shrinks
          rather than pushing the record sideways. */}
      <div className={s.stage}>
        <div
          className={s.frame}
          style={{ width: chosen.px, maxWidth: '100%' }}
          data-testid="kb-preview-frame"
          data-width={chosen.key}
        >
          <KbArticleBody
            articleId={articleId}
            body={article.body}
            bodyDocument={article.body_document}
            assets={assets}
            assetsLoading={assetsLoading}
            assetsFailed={assetsFailed}
            requestUrl={requestUrl ?? (() => Promise.reject(new Error('that figure is not available')))}
          />
        </div>
      </div>

      <p className={s.caveat} data-testid="kb-preview-caveat">
        Figures are fetched under your own session and checked against what the
        server verified, exactly as a reader’s are. Looking at one is recorded.
      </p>
    </section>
  )
}
